import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react"

import type { MetadataFilterMatchType } from "@/constants/filter-types"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { useQueryParams } from "@/hooks/useQueryParams"
import { validateCustomDateRange } from "@/utils/date/dateRange"

import type { GlobalDateRange } from "../types/core"
import {
  calculateRelativeDashboardDateRange,
  parseRelativeDashboardDateRange,
  serializeRelativeDashboardDateRange,
  type RelativeDashboardDateRange,
} from "../utils/relativeDashboardDateRange"

// Metadata filter type
interface MetadataFilter {
  key: string
  value: string
  matchType: MetadataFilterMatchType
}

// State interface
interface DashboardFiltersState {
  dateRange: GlobalDateRange | null
  // New override-related state
  globalDateRangeOverride: GlobalDateRange | null
  relativeDateRangeOverride: RelativeDashboardDateRange | null
  isOverrideActive: boolean
  metadataFilters: MetadataFilter[]
}

// Action types
type DashboardFiltersAction =
  | { type: "SET_DATE_RANGE"; payload: GlobalDateRange | null }
  // New override-related actions
  | {
      type: "SET_GLOBAL_OVERRIDE"
      payload: GlobalDateRange | null
      relative?: RelativeDashboardDateRange | null
    }
  | { type: "CLEAR_GLOBAL_OVERRIDE" }
  | { type: "ADD_METADATA_FILTER"; payload: MetadataFilter }
  | { type: "REMOVE_METADATA_FILTER"; payload: number }
  | { type: "CLEAR_METADATA_FILTERS" }

function parseUrlDateRange(
  startParam: string,
  endParam: string,
): GlobalDateRange | null {
  const dateRange = {
    start: new Date(startParam),
    end: new Date(endParam),
  }

  if (isNaN(dateRange.start.getTime()) || isNaN(dateRange.end.getTime())) {
    return null
  }

  const validation = validateCustomDateRange(
    dateRange.start,
    dateRange.end,
    "fixed",
    { allowSharedEndOfDay: true },
  )
  if (!validation.isValid) {
    return null
  }

  return dateRange
}

// Helper function to initialize state from URL parameters
const getInitialState = (
  queryParams: ReturnType<typeof useQueryParams>,
): DashboardFiltersState => {
  const initialState: DashboardFiltersState = {
    dateRange: null, // Default fallback
    // Initialize override state
    globalDateRangeOverride: null,
    relativeDateRangeOverride: null,
    isOverrideActive: false,
    metadataFilters: [],
  }

  const startParam = queryParams.get("start")
  const endParam = queryParams.get("end")
  const relative = parseRelativeDashboardDateRange(
    queryParams.get("dashboardRange"),
  )

  // Clean up partial or malformed params
  if ((startParam && !endParam) || (!startParam && endParam)) {
    queryParams.setMultiple({ start: null, end: null, dashboardRange: null })
    return initialState
  }

  if (startParam && endParam) {
    const dateRange = parseUrlDateRange(startParam, endParam)
    if (!dateRange) {
      queryParams.setMultiple({ start: null, end: null, dashboardRange: null })
      return initialState
    }

    initialState.globalDateRangeOverride = relative
      ? calculateRelativeDashboardDateRange(relative)
      : dateRange
    initialState.relativeDateRangeOverride = relative
    initialState.isOverrideActive = true
  }

  return initialState
}

// Reducer
const dashboardFiltersReducer = (
  state: DashboardFiltersState,
  action: DashboardFiltersAction,
): DashboardFiltersState => {
  switch (action.type) {
    case "SET_DATE_RANGE":
      return {
        ...state,
        dateRange: action.payload,
      }
    case "SET_GLOBAL_OVERRIDE":
      return {
        ...state,
        globalDateRangeOverride: action.payload,
        relativeDateRangeOverride: action.payload
          ? (action.relative ?? null)
          : null,
        isOverrideActive: action.payload !== null,
      }
    case "CLEAR_GLOBAL_OVERRIDE":
      return {
        ...state,
        globalDateRangeOverride: null,
        relativeDateRangeOverride: null,
        isOverrideActive: false,
      }
    case "ADD_METADATA_FILTER":
      return {
        ...state,
        metadataFilters: [...state.metadataFilters, action.payload],
      }
    case "REMOVE_METADATA_FILTER":
      return {
        ...state,
        metadataFilters: state.metadataFilters.filter(
          (_, i) => i !== action.payload,
        ),
      }
    case "CLEAR_METADATA_FILTERS":
      return { ...state, metadataFilters: [] }
    default:
      return state
  }
}

// Context interface
interface DashboardFiltersContextType {
  state: DashboardFiltersState
  handlerSetDateRange: (dateRange: GlobalDateRange | null) => void
  // Override-related handlers
  handlerSetGlobalOverride: (
    dateRange: GlobalDateRange | null,
    relative?: RelativeDashboardDateRange | null,
  ) => void
  handlerClearGlobalOverride: () => void
  // Metadata filter handlers
  handlerAddMetadataFilter: (filter: MetadataFilter) => void
  handlerRemoveMetadataFilter: (index: number) => void
  handlerClearMetadataFilters: () => void
}

// Create context
const DashboardFiltersContext = createContext<
  DashboardFiltersContextType | undefined
>(undefined)

// Provider component
interface DashboardFiltersProviderProps {
  children: React.ReactNode
}

export const DashboardFiltersProvider: React.FC<
  DashboardFiltersProviderProps
> = ({ children }) => {
  const queryParams = useQueryParams()

  // Get URL param values for proper memoization
  const urlStart = queryParams.get("start")
  const urlEnd = queryParams.get("end")
  const urlDashboardRange = queryParams.get("dashboardRange")

  // Initialize state synchronously from URL parameters
  // Note: getInitialState already reads from queryParams internally, so we only
  // need queryParams as a dependency. urlStart/urlEnd changes will trigger via queryParams.
  const initialState = useMemo(
    () => getInitialState(queryParams),
    [queryParams],
  )

  const [state, dispatch] = useReducer(dashboardFiltersReducer, initialState)
  const { trackEvent } = useAnalytics()
  const stateRef = useRef(state)
  const lastUrlRef = useRef<string | null>(null)

  useEffect(() => {
    stateRef.current = state
  }, [state])

  useEffect(() => {
    const urlKey = JSON.stringify([urlStart, urlEnd, urlDashboardRange])
    if (lastUrlRef.current === urlKey) {
      return
    }
    lastUrlRef.current = urlKey
    if (urlStart && urlEnd) {
      const dateRange = parseUrlDateRange(urlStart, urlEnd)
      if (!dateRange) {
        queryParams.setMultiple({
          start: null,
          end: null,
          dashboardRange: null,
        })
        return
      }
      const relative = parseRelativeDashboardDateRange(urlDashboardRange)
      const current = stateRef.current
      const currentRelative = current.relativeDateRangeOverride
      if (
        current.isOverrideActive &&
        ((relative &&
          currentRelative &&
          serializeRelativeDashboardDateRange(relative) ===
            serializeRelativeDashboardDateRange(currentRelative)) ||
          (!relative &&
            !currentRelative &&
            current.globalDateRangeOverride?.start.getTime() ===
              dateRange.start.getTime() &&
            current.globalDateRangeOverride?.end.getTime() ===
              dateRange.end.getTime()))
      ) {
        return
      }
      dispatch({
        type: "SET_GLOBAL_OVERRIDE",
        payload: relative
          ? calculateRelativeDashboardDateRange(relative)
          : dateRange,
        relative,
      })
    }
  }, [queryParams, urlStart, urlEnd, urlDashboardRange])

  // Handlers that update both state and URL
  const handlerSetDateRange = useCallback(
    (dateRange: GlobalDateRange | null) => {
      const previousDateRange = state.dateRange
      dispatch({ type: "SET_DATE_RANGE", payload: dateRange })

      // Track date range change
      if (dateRange && previousDateRange) {
        trackEvent(TRACKING_EVENTS.DASHBOARD.DATE_RANGE_CHANGED, {
          previous_start: previousDateRange.start.toISOString(),
          previous_end: previousDateRange.end.toISOString(),
          new_start: dateRange.start.toISOString(),
          new_end: dateRange.end.toISOString(),
        })
      }

      // Update URL when user changes date range (batch to avoid stale-params loop)
      if (dateRange) {
        queryParams.setMultiple({
          start: dateRange.start.toISOString(),
          end: dateRange.end.toISOString(),
        })
      } else {
        queryParams.setMultiple({ start: null, end: null })
      }
    },
    [queryParams, state.dateRange, trackEvent],
  )

  // New override-related handlers
  const handlerSetGlobalOverride = useCallback(
    (
      dateRange: GlobalDateRange | null,
      relative?: RelativeDashboardDateRange | null,
    ) => {
      dispatch({ type: "SET_GLOBAL_OVERRIDE", payload: dateRange, relative })

      // Track global override activation/deactivation
      if (dateRange) {
        trackEvent(TRACKING_EVENTS.DASHBOARD.GLOBAL_OVERRIDE_ACTIVATED, {
          override_start: dateRange.start.toISOString(),
          override_end: dateRange.end.toISOString(),
        })
      } else {
        trackEvent(TRACKING_EVENTS.DASHBOARD.GLOBAL_OVERRIDE_DEACTIVATED, {})
      }

      // Update URL when global override changes (batch to avoid stale-params loop)
      if (dateRange) {
        queryParams.setMultiple({
          start: dateRange.start.toISOString(),
          end: dateRange.end.toISOString(),
          dashboardRange: relative
            ? serializeRelativeDashboardDateRange(relative)
            : "fixed",
        })
      } else {
        queryParams.setMultiple({
          start: null,
          end: null,
          dashboardRange: null,
        })
      }
    },
    [queryParams, trackEvent],
  )

  const handlerClearGlobalOverride = useCallback(() => {
    dispatch({ type: "CLEAR_GLOBAL_OVERRIDE" })

    // Track global override deactivation
    trackEvent(TRACKING_EVENTS.DASHBOARD.GLOBAL_OVERRIDE_DEACTIVATED, {})

    queryParams.setMultiple({ start: null, end: null, dashboardRange: null })
  }, [queryParams, trackEvent])

  const handlerAddMetadataFilter = useCallback((filter: MetadataFilter) => {
    dispatch({ type: "ADD_METADATA_FILTER", payload: filter })
  }, [])

  const handlerRemoveMetadataFilter = useCallback((index: number) => {
    dispatch({ type: "REMOVE_METADATA_FILTER", payload: index })
  }, [])

  const handlerClearMetadataFilters = useCallback(() => {
    dispatch({ type: "CLEAR_METADATA_FILTERS" })
  }, [])

  const contextValue = useMemo<DashboardFiltersContextType>(
    () => ({
      state,
      handlerSetDateRange,
      handlerSetGlobalOverride,
      handlerClearGlobalOverride,
      handlerAddMetadataFilter,
      handlerRemoveMetadataFilter,
      handlerClearMetadataFilters,
    }),
    [
      state,
      handlerSetDateRange,
      handlerSetGlobalOverride,
      handlerClearGlobalOverride,
      handlerAddMetadataFilter,
      handlerRemoveMetadataFilter,
      handlerClearMetadataFilters,
    ],
  )

  return (
    <DashboardFiltersContext.Provider value={contextValue}>
      {children}
    </DashboardFiltersContext.Provider>
  )
}

// Custom hook
export const useDashboardFilters = () => {
  const context = useContext(DashboardFiltersContext)
  if (context === undefined) {
    throw new Error(
      "useDashboardFilters must be used within a DashboardFiltersProvider",
    )
  }
  return context
}
